import { useEffect, useState } from "react";

import { useLocale } from "@/libraries/web-framework";
import { WAC_TO_CPAPI_LOCALE_MAP } from "@/utils/content-queries/constants.ts";

import {
    getTemplateCategories,
    JiraTemplateCategory
} from "./get-template-categories.ts";

interface CacheEntry {
    data: JiraTemplateCategory[];
    promise: Promise<JiraTemplateCategory[]> | null;
}

// cache data per locale to optimize duplicate queries
const cache = new Map<string, CacheEntry>();

// exposed for testing purposes
export const clearCache = () => {
    cache.clear();
};

export const useJiraTemplateCategories = () => {
    const locale = useLocale();
    const mappedLocale = WAC_TO_CPAPI_LOCALE_MAP[locale];

    // initialize state from cache if available
    const cacheEntry = mappedLocale ? cache.get(mappedLocale) : undefined;
    const [data, setData] = useState<JiraTemplateCategory[]>(
        cacheEntry?.data ?? []
    );
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<unknown>(null);

    useEffect(() => {
        // if we don't have a mapped locale, do nothing
        if (!mappedLocale) {
            return;
        }

        const existingCache = cache.get(mappedLocale);

        // if we already have cached data, use it
        if (existingCache?.data && !existingCache.promise) {
            setData(existingCache.data);
            return;
        }

        // if a fetch is already in progress, wait for it
        if (existingCache?.promise) {
            setLoading(true);
            existingCache.promise
                .then((items) => {
                    setData(items);
                    setLoading(false);
                })
                .catch((err) => {
                    setError(err);
                    setLoading(false);
                });
            return;
        }

        // start a new fetch
        setLoading(true);
        setError(null);

        const fetchPromise = getTemplateCategories({
            // @ts-ignore
            locale: mappedLocale
        })
            .then((response) => {
                const items =
                    response.taxonomyTemplateCategoryCollection.items ?? [];

                // update cache with data and clear promise
                cache.set(mappedLocale, { data: items, promise: null });
                setData(items);
                setLoading(false);
                return items;
            })
            .catch((err) => {
                // clear promise on error
                const entry = cache.get(mappedLocale);
                if (entry) {
                    entry.promise = null;
                }
                setError(err);
                setLoading(false);
                throw err;
            });

        // store the in-flight promise
        cache.set(mappedLocale, {
            data: existingCache?.data ?? [],
            promise: fetchPromise
        });
    }, [locale, mappedLocale]);

    return { data, loading, error };
};
